<template>
    <!-- eslint-disable-next-line vue/no-v-html -->
    <div
        class="flex items-center justify-center"
        :style="sizingStyle"
        aria-hidden="true"
        role="presentation"
        v-html="svgContent"
    />
</template>

<script>
import * as Sentry from '@sentry/browser';

/**
 * Every icon ships inside the bundle, so a control's artwork draws with the
 * connection gone and after a deploy has replaced the files the page
 * was loaded from.
 */
const ICONS = import.meta.glob('../../images/icons/*.svg', { query: '?raw', import: 'default', eager: true });

/** An unknown name is one bug however many controls draw it, so it is reported once per page load. */
const reportedUnknownNames = new Set();

const toCssSize = size => {
    if (size === '' || size == null) return '24px';
    return Number.isFinite(Number(size)) ? `${Number(size)}px` : size;
};

export default {
    props: {
        icon: {
            type: String,
            required: true,
        },
        width: {
            type: [String, Number],
            default: 24,
        },
        height: {
            type: [String, Number],
            default: 24,
        },
        fill: {
            type: String,
            default: null,
        },
        iconClass: {
            type: String,
            default: '',
        },
    },
    computed: {
        svgContent() {
            return ICONS[`../../images/icons/${this.icon}.svg`] ?? '';
        },
        sizingStyle() {
            return {
                height: toCssSize(this.height),
                width: toCssSize(this.width),
            };
        },
    },
    watch: {
        icon: {
            // A name built at runtime can miss the set, and the box it leaves is
            // otherwise silent: no fetch fails and no test can see the name.
            handler(icon) {
                if (!icon || this.svgContent || reportedUnknownNames.has(icon)) return;

                reportedUnknownNames.add(icon);
                Sentry.captureMessage('icons.unknown_name', { level: 'warning', extra: { icon } });
            },
            immediate: true,
        },
        fill() {
            this.applyStyling();
        },
        // iconClass never re-renders the template, and applyStyling only ever adds a class.
        iconClass(_, previousClass) {
            if (previousClass) this.$el.firstChild?.classList.remove(previousClass);
            this.applyStyling();
        },
    },
    mounted() {
        this.applyStyling();
    },
    // A swapped icon arrives as new markup and a new size only reaches the
    // wrapper, so after either re-render the svg has to be styled again.
    updated() {
        this.applyStyling();
    },
    methods: {
        applyStyling() {
            if (!this.$el || !this.$el.firstChild) {
                return;
            }

            this.$el.style.pointerEvents = 'none';

            const svgElement = this.$el.firstChild;
            if (svgElement) {
                if (this.iconClass) {
                    svgElement.classList.add(this.iconClass);
                }

                svgElement.removeAttribute('width');
                svgElement.style.width = this.sizingStyle.width;

                svgElement.removeAttribute('height');
                svgElement.style.height = this.sizingStyle.height;

                // The inline fill beats the artwork's own fill attribute, which stays
                // so an outline can fall back to it once the call site clears its colour.
                if (this.fill) {
                    svgElement.style.fill = this.fill;
                } else {
                    svgElement.style.fill = svgElement.getAttribute('fill') === 'none' ? '' : 'currentColor';
                }

                // Apply stroke inheritance for icons that use stroke instead of fill
                if (svgElement.getAttribute('stroke') === 'currentColor') {
                    svgElement.style.stroke = 'currentColor';
                }

                svgElement.setAttribute('aria-hidden', 'true');
                svgElement.setAttribute('focusable', 'false');
            }
        },
    },
};
</script>

<style>
/* The svg arrives through v-html, so the template cannot give it a class. */
svg {
    vertical-align: middle;
    width: 24px;
    height: 24px;
}
</style>
